import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AppContext = createContext(null);

export function AppProvider({ children }) {
    const { user, isChild } = useAuth();
    const [children_, setChildren] = useState([]);
    const [activeChildId, setActiveChildId] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadChildren = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data } = await api.get("/children");
            setChildren(data);
            if (isChild) {
                // child always has only own entry
                setActiveChildId(user.id);
            } else {
                if (data.length && !activeChildId) setActiveChildId(data[0].id);
                if (activeChildId && !data.find((c) => c.id === activeChildId)) {
                    setActiveChildId(data[0]?.id || null);
                }
            }
        } finally {
            setLoading(false);
        }
    }, [user, isChild, activeChildId]);

    useEffect(() => {
        if (user) loadChildren();
        else {
            setChildren([]);
            setActiveChildId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const refresh = () => loadChildren();

    return (
        <AppContext.Provider
            value={{
                children: children_,
                activeChildId,
                setActiveChildId,
                refreshChildren: refresh,
                loading,
            }}
        >
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used inside AppProvider");
    return ctx;
}
